/*
 * Copyright 2022 Benjamin Menant.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {Page, usePromise} from "@keycloak/keycloak-account-ui";
import {useState} from "react";
import {
    EmptyState,
    EmptyStateFooter,
    EmptyStateActions,
    EmptyStateBody,
    EmptyStateIcon,
    PageSection,
    PageSectionVariants,
    Spinner,
    Title,
} from "@patternfly/react-core";
import {UnknownIcon} from "@patternfly/react-icons";
import {useTranslation} from "react-i18next";
import { IAMTaxReceiptJsonSchema } from '../../donor-service/types/IAMTaxReceiptJsonSchema';
import {useEnvironmentWithDonorService} from "../../context.tsx";
import { TaxReceipt } from "./TaxReceipt";

type DonorTaxReceipts = IAMTaxReceiptJsonSchema[];

export function TaxReceiptsPage () {
    const { t } = useTranslation();
    const context = useEnvironmentWithDonorService();

    const [taxReceipts, setTaxReceipts] = useState<DonorTaxReceipts>();

    usePromise(
        async () => {
            const { taxReceipts } = await context.donorService.fetchDonorRecord();
            return taxReceipts;
        },
        setTaxReceipts,
    );

    if (!taxReceipts) {
        return <Spinner />;
    }

    const hasAnyReceipt = taxReceipts.length > 0;

    return (
        <Page title={t("taxReceipts")} description={t("taxReceiptsIntro")}>
            <PageSection variant={PageSectionVariants.light} padding={{ default: 'noPadding' }}>
                {hasAnyReceipt
                    ? groupByYear(taxReceipts)
                        .map(([year, receipts]) => <TaxReceipt key={year} year={year} receipts={receipts} />)
                    : <EmptyState variant="xl">
                        <EmptyStateIcon icon={UnknownIcon} />
                        <Title headingLevel="h2" size="2xl">
                            {t("noTaxReceipt")}
                        </Title>
                        <EmptyStateBody>
                            {t("missingTaxReceipt")}
                        </EmptyStateBody>
                        <EmptyStateFooter>
                            <EmptyStateActions>
                                <strong>{t("reportMissingTaxReceipt")}</strong>
                            </EmptyStateActions>
                        </EmptyStateFooter>
                    </EmptyState>
                }
            </PageSection>
        </Page>
    );
}

function groupByYear (receipts: DonorTaxReceipts): (readonly [number, DonorTaxReceipts])[] {
    return Object
        .entries(
            receipts.reduce<Record<number, DonorTaxReceipts>>((acc, curr) => {
                const year = Number(curr.dateIssued.slice(0, 4)) - 1; // Edited the year after the donations were made
                return Object.assign(acc, { [year]: [ ...acc[year] || [], curr ] });
            }, {}),
        )
        .map(([year, receipts]) => [Number(year), receipts] as const)
        .sort(([a], [b]) => b - a);
}
